import { api } from './apiService';
import { getApiUrl } from '../config/environments';

const API_BASE_URL = getApiUrl();

/**
 * Feedback Service
 * Handles API calls for user feedback submission
 */

/**
 * Submit feedback ticket
 * @param {object} feedbackData - Feedback data
 * @param {string} feedbackData.type - Type: 'bug', 'feature', or 'feedback'
 * @param {string} feedbackData.title - Title
 * @param {string} feedbackData.description - Description
 * @param {string} feedbackData.priority - Priority (bug only)
 * @param {string} feedbackData.category - Category (bug only)
 * @param {string} feedbackData.short_description - Short description (feature only)
 * @param {number} feedbackData.star_rating - Star rating 1-5 (feedback only)
 * @param {array} feedbackData.screenshot_ids - Screenshot IDs (bug only)
 * @returns {Promise<object>} Response with ticket details
 */
export const submitFeedback = async (feedbackData) => {
  try {
    // Auto-collect metadata
    const metadata = {
      submitted_from_url: window.location.href,
      user_agent: navigator.userAgent,
      browser_info: getBrowserInfo(),
      app_version: import.meta.env.VITE_APP_VERSION || '1.0.0'
    };

    const payload = {
      ...feedbackData,
      ...metadata
    };

    const response = await api.post(`${API_BASE_URL}/api/feedback`, payload);

    return response.data;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error.response?.data || error;
  }
};

/**
 * Upload screenshot for ticket
 * @param {File} file - Image file
 * @returns {Promise<object>} Response with file URL and ID
 */
export const uploadScreenshot = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `${API_BASE_URL}/api/feedback/upload-screenshot`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading screenshot:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get user's submitted tickets
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<object>} Response with tickets and pagination
 */
export const getMyTickets = async (page = 1, limit = 25) => {
  try {
    const response = await api.get(`${API_BASE_URL}/api/feedback/my-tickets`, {
      params: { page, limit }
    });

    return response.data;
  } catch (error) {
    console.error('Error getting my tickets:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get browser information
 * @returns {object} Parsed browser info
 */
function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';

  // Detect browser
  if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Chrome') > -1) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Safari') > -1) {
    browser = 'Safari';
    version = ua.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
    browser = 'Internet Explorer';
    version = ua.match(/(?:MSIE |rv:)([0-9.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Edge') > -1) {
    browser = 'Edge';
    version = ua.match(/Edge\/([0-9.]+)/)?.[1] || 'Unknown';
  }

  // Detect OS
  if (ua.indexOf('Windows') > -1) {
    os = 'Windows';
    if (ua.indexOf('Windows NT 10.0') > -1) os = 'Windows 10/11';
    else if (ua.indexOf('Windows NT 6.3') > -1) os = 'Windows 8.1';
    else if (ua.indexOf('Windows NT 6.2') > -1) os = 'Windows 8';
    else if (ua.indexOf('Windows NT 6.1') > -1) os = 'Windows 7';
  } else if (ua.indexOf('Mac') > -1) {
    os = 'macOS';
  } else if (ua.indexOf('Linux') > -1) {
    os = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    os = 'Android';
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    os = 'iOS';
  }

  return {
    browser,
    version,
    os,
    userAgent: ua,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height
    }
  };
}

export default {
  submitFeedback,
  uploadScreenshot,
  getMyTickets
};

